// Public REST DTOs for the run page's recovery actions and the connection
// exhaustion state (contract docs/plans/2026-10-02-checkpoints-contract.md §7).

import { z } from 'zod'
import type { RunStatus } from '../constants/index.js'
import type { ConnectionExhaustedReason, RunnerFailure } from './failure.js'
import type { RunCheckpointKind, RunPauseReason, RunWorklog } from './checkpoint.js'
import type { HandoffReason } from './payload.js'

/** A checkpoint without its blobs, for the UI. */
export interface RunCheckpointSummary {
  id: string
  runId: string
  kind: RunCheckpointKind
  reason: RunPauseReason | null
  runner: string
  agentId: string | null
  connectionId: string | null
  failure: RunnerFailure | null
  worklog: RunWorklog | null
  summary: string | null
  /** Whether the encrypted patch/session are still there (false once wiped). */
  hasWorkspacePatch: boolean
  hasSession: boolean
  resumeAfter: string | null
  expiresAt: string
  resumedAt: string | null
  createdAt: string
}

/** One run of a handoff chain (oldest first). */
export interface RunChainEntry {
  runId: string
  attempt: number
  agentId: string
  agentName: string | null
  model: string | null
  status: RunStatus
  /** Why this run handed off to the next one; null for the last run of the chain. */
  handoffReason: HandoffReason | null
  startedAt: string | null
  endedAt: string | null
}

/** An agent the run could be handed to (same role, other agent). */
export interface HandoffCandidate {
  agentId: string
  name: string
  model: string | null
  runner: string | null
  connectionId: string | null
  /** False when its connection is exhausted, or the chain is at `maxHandoffs`. */
  available: boolean
  exhaustedUntil: string | null
  exhaustedReason: ConnectionExhaustedReason | null
  /** Listed in the role's `onQuota.fallbackAgentIds` (in that order first). */
  fallback: boolean
}

/** GET /runs/:id/recovery */
export interface RunRecoveryState {
  runId: string
  status: RunStatus
  /** The checkpoint "resume"/"hand off" would use (latest usable one), or null. */
  checkpoint: RunCheckpointSummary | null
  /** Every checkpoint of this run, newest first (no blobs). */
  checkpoints: RunCheckpointSummary[]
  /** Quota-paused (or manually scheduled) run: when it resumes by itself. */
  resumeAfter: string | null
  /** The failure that stopped the run, if any (latest `failure` checkpoint). */
  failure: RunnerFailure | null
  /** The whole handoff chain this run belongs to, oldest first (just this run when there is none). */
  chain: RunChainEntry[]
  actions: {
    /** "Resume now": paused (not waiting on an approval), failed or blocked, with a usable checkpoint. */
    resumeNow: boolean
    /** "Resume at …": same, scheduling `resume_after` instead. */
    resumeAt: boolean
    /** "Hand off to …": paused/failed/blocked, chain below `maxHandoffs`. */
    handoff: boolean
  }
  handoffCandidates: HandoffCandidate[]
}

/** POST /runs/:id/resume — no `at` = now; `at` = schedule (sets `resume_after`, the run stays/turns `paused`). */
export const ResumeRunRequestSchema = z.object({
  at: z.string().datetime({ offset: true }).optional(),
})
export type ResumeRunRequest = z.infer<typeof ResumeRunRequestSchema>

/** POST /runs/:id/handoff */
export const HandoffRunRequestSchema = z.object({
  agentId: z.string().uuid(),
})
export type HandoffRunRequest = z.infer<typeof HandoffRunRequestSchema>

/** 202 response of both actions. */
export interface RunRecoveryActionResponse {
  accepted: true
  runId: string
  /** The new run, for a handoff. */
  childRunId: string | null
  resumeAfter: string | null
}

/** POST /executor-connections/:id/exhaustion/clear → 200 with the connection's new state. */
export interface ClearConnectionExhaustionResponse {
  id: string
  exhaustedUntil: null
  exhaustedReason: null
}
